import { Form } from 'formik';
import { assign, keys, map, pick } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  CloseFormikDialogResult,
  DialogProps,
  FormControls,
  FormikCheckbox,
  FormikDialog,
  FormikSelect,
  FormikTextField,
} from 'components';
import { SciTheme, SciView } from 'enums';
import { TranslationNamespace } from 'i18n';
import { SciDomainDto } from 'types';

import { CustomerBanksConfig } from './CustomerBanksConfig';

type Values = SciDomainDto;

type Props = DialogProps<CloseFormikDialogResult<Values>>;

export const SciDomainsDialog: React.FC<Props> = ({
  open,
  data,
  onClose,
  ...rest
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.sci_domains',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const title = useMemo(
    () =>
      data ? t('details_dialog.edit_title') : t('details_dialog.create_title'),
    [t, data],
  );

  const themesOptions = useMemo(
    () =>
      map(SciTheme, (theme) => ({
        value: theme,
        label: theme,
      })),
    [],
  );

  const viewsOptions = useMemo(
    () =>
      map(SciView, (view) => ({
        value: view,
        label: t(`views.${view}`, { defaultValue: view }),
      })),
    [t],
  );

  const getInitialValues = useCallback(
    (): Values => ({
      url: '',
      logo: '',
      loader: '',
      title: '',
      copyright: '',
      theme: '',
      view: '',
      shopSCITradeMethodsOnly: false,
      telegramSupportBotUrl: '',
      customerBankConfigs: [],
    }),
    [],
  );

  const [initialValues, setInitialValues] = useState(getInitialValues());

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        url: Yup.string().required(tCommon('errors.required')),
        logo: Yup.string(),
        loader: Yup.string(),
        title: Yup.string(),
        copyright: Yup.string(),
        theme: Yup.string().oneOf(Object.values(SciTheme)),
        view: Yup.string().oneOf(Object.values(SciView)),
        shopSCITradeMethodsOnly: Yup.boolean(),
        telegramSupportBotUrl: Yup.string(),
        customerBankConfigs: Yup.array().of(
          Yup.object().shape({
            bank: Yup.string().required(tCommon('errors.required')),
            customerBanks: Yup.array()
              .of(Yup.string().required(tCommon('errors.required')))
              .required(tCommon('errors.required')),
          }),
        ),
      }),
    [tCommon],
  );

  useEffect(() => {
    const _initialValues = getInitialValues();
    setInitialValues(assign(_initialValues, pick(data, keys(_initialValues))));
  }, [data, getInitialValues]);

  return (
    <FormikDialog
      dialogProps={{
        open,
        title,
        onClose,
        maxWidth: 'sm',
      }}
      formikProps={{
        initialValues,
        validateOnMount: true,
        validationSchema,
      }}
    >
      {() => (
        <Form>
          <FormControls>
            <FormikTextField label={t('fields.url')} name="url" required />
            <FormikTextField label={t('fields.logo')} name="logo" />
            <FormikTextField label={t('fields.loader')} name="loader" />
            <FormikTextField label={t('fields.title')} name="title" />
            <FormikTextField label={t('fields.copyright')} name="copyright" />
            <FormikSelect
              label={t('fields.theme')}
              name="theme"
              options={themesOptions}
              noneOption
            />
            <FormikSelect
              label={t('fields.view')}
              name="view"
              options={viewsOptions}
              noneOption
            />
            <FormikTextField
              label={t('fields.telegramSupportBotUrl')}
              name="telegramSupportBotUrl"
            />
            <FormikCheckbox
              label={t('fields.shop_payment_types_only')}
              name="shopSCITradeMethodsOnly"
            />
            <CustomerBanksConfig />
          </FormControls>
        </Form>
      )}
    </FormikDialog>
  );
};
